import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FitnessPlanService } from '../fitness-plan/fitness-plan.service';
import { DateUtilityService } from './date-utility.service';
import { ExerciseDataService } from './exercise-data.service';
import { ExerciseSet } from '../models/exercise-set.interface';

export interface Exercise {
  id: number;
  exercise: string;
  exerciseId?: string;
  allotmentId?: string;
  sets: ExerciseSet[];
  section?: 'warmup' | 'main' | 'cooldown';
  isMarkedForDeletion?: boolean;
}

export interface DateItem {
  date: Date;
  formattedDate: string;
  exercises: Exercise[];
  isRestDay?: boolean;
  restDayAllotmentId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FitnessPlanPersistenceService {
  constructor(
    private fitnessPlanService: FitnessPlanService,
    private dateUtilityService: DateUtilityService,
    private exerciseDataService: ExerciseDataService
  ) {}

  /**
   * Saves the fitness plan by first deleting marked exercises and pending deletions, then saving the plan
   */
  saveFitnessPlan(
    dateList: DateItem[],
    memberId: string,
    availableExercisesData: any[],
    isEditMode: boolean,
    // helperService: any,
    pendingDeletions?: Set<string>,
    fitnessPlanName?: string
  ): Observable<any> {
    // Collect all IDs to delete (both marked exercises and pending deletions)
    return new Observable((observer) => {
      const allIdsToDelete: string[] = [];

      // Get marked exercise IDs
      dateList.forEach((dateItem) => {
        dateItem.exercises.forEach((exercise) => {
          if (exercise.isMarkedForDeletion && exercise.allotmentId) {
            allIdsToDelete.push(exercise.allotmentId);
          }
        });
      });

      // Add pending deletion IDs (e.g., rest day allotments)
      if (pendingDeletions && pendingDeletions.size > 0) {
        allIdsToDelete.push(...Array.from(pendingDeletions));
      }

      console.log('All IDs to delete:', allIdsToDelete);

      if (allIdsToDelete.length > 0) {
        // Delete all marked items
        this.fitnessPlanService
          .deleteExerciseAllotment(allIdsToDelete)
          .subscribe({
            next: (deleteResponse) => {
              console.log('All deletions response:', deleteResponse);

              // Remove deleted items from local data
              this.removeMarkedExercises(dateList, pendingDeletions);

              // Now generate and save the fitness plan
              const exerciseArray = this.generateExerciseArray(
                dateList,
                memberId,
                availableExercisesData,
                fitnessPlanName
              );

              // The create endpoint acts as upsert (insert or update)
              this.fitnessPlanService
                .createFitnessPlan(exerciseArray)
                .subscribe({
                  next: (saveResponse) => {
                    observer.next(saveResponse);
                    observer.complete();
                  },
                  error: (saveError) => {
                    observer.error(saveError);
                  },
                });
            },
            error: (deleteError) => {
              console.error('Error deleting items:', deleteError);
              observer.error(deleteError);
            },
          });
      } else {
        // No deletions needed, proceed directly to saving
        const exerciseArray = this.generateExerciseArray(
          dateList,
          memberId,
          availableExercisesData,
          fitnessPlanName
        );


        this.fitnessPlanService
          .createFitnessPlan(exerciseArray)
          .subscribe({
            next: (saveResponse) => {
              observer.next(saveResponse);
              observer.complete();
            },
            error: (saveError) => {
              observer.error(saveError);
            },
          });
      }
    });
  }

  /**
   * Generates the exercise array in the format required by the API
   */
  generateExerciseArray(
    dateList: DateItem[],
    memberId: string,
    availableExercisesData: any[],
    fitnessPlanName?: string
    // helperService: any
  ): any {
    const exerciseArray: any[] = [];
    const currentUserId = this.fitnessPlanService.getCurrentUserId();

    // Get today's date for filtering
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    dateList.forEach((dateItem) => {
      const dateString = this.dateUtilityService.formatDateForAPI(
        dateItem.date
      );

      // Skip past dates when updating fitness plans (only send current and future dates)
      const itemDate = new Date(dateItem.date);
      itemDate.setHours(0, 0, 0, 0);

      if (itemDate < today) {
        console.log(
          `Skipping past date ${dateItem.formattedDate} - not sending to API`
        );
        return;
      }

      if (dateItem.isRestDay) {
        console.log(
          `Creating rest day allotment for: ${dateItem.formattedDate}`
        );

        // Create a special rest day allotment
        const restDayAllotment: any = {
          memberId: memberId,
          exerciseId: '', // Empty string for rest day
          phase: 'restday', // Mark as rest day phase
          date: dateString,
          assignedBy: currentUserId,
          sets: [], // Empty sets array for rest day
        };

        // Include existing rest day allotment ID if updating
        if (dateItem.restDayAllotmentId) {
          restDayAllotment.id = dateItem.restDayAllotmentId;
          console.log(
            `Including existing rest day allotment ID: ${dateItem.restDayAllotmentId}`
          );
        }

        exerciseArray.push(restDayAllotment);
        console.log('Created rest day allotment:', restDayAllotment);
        return;
      }

      dateItem.exercises.forEach((exercise) => {
        if (!exercise.exercise.trim()) {
          console.log('Skipping empty exercise');
          return;
        }

        // Skip exercises marked for deletion
        if (exercise.isMarkedForDeletion) {
          console.log(
            'Skipping exercise marked for deletion:',
            exercise.exercise
          );
          return;
        }

        // Find exercise ID by name
        const exerciseData = availableExercisesData.find(
          (ex) => ex.name === exercise.exercise
        );
        if (!exerciseData) {
          console.log(
            'Exercise not found in available exercises:',
            exercise.exercise
          );
          return;
        }

        // Filter out empty sets - handle both string and number types for reps, time, and weights
        const validSets = exercise.sets.filter((set) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          const timeValue =
            typeof set.time === 'string'
              ? set.time.trim()
              : set.time !== null && set.time !== undefined
              ? String(set.time).trim()
              : '';

          const weightsValue =
            typeof set.weights === 'string'
              ? set.weights.trim()
              : set.weights !== null && set.weights !== undefined
              ? String(set.weights).trim()
              : '';

          // A set is valid if it has either valid reps OR valid time OR valid weights
          const hasValidReps =
            repsValue && repsValue !== '0' && repsValue !== '';
          const hasValidTime =
            timeValue && timeValue !== '0' && timeValue !== '';
          const hasValidWeights =
            weightsValue && weightsValue !== '0' && weightsValue !== '';

          return hasValidReps || hasValidTime || hasValidWeights;
        });

        if (validSets.length === 0) {
          console.log('No valid sets found for exercise:', exercise.exercise);
          return;
        }

        // Create a single allotment with nested sets array (matching DB structure)
        const exerciseAllotment: any = {
          memberId: memberId,
          exerciseId: exerciseData.id,
          sets: validSets.map((set, setIndex) => ({
            time: ExerciseDataService.convertTimeToSeconds(String(set.time)),
            reps: parseInt(String(set.reps)) || 0,
            weights: set.weights ? parseInt(String(set.weights)) : 0,
            status:
              typeof set.status === 'string' && set.status.trim() !== ''
                ? set.status
                : 'new',
            seq: setIndex + 1,
          })),
          phase: this.exerciseDataService.mapSectionToPhase(
            exercise.section || 'main'
          ),
          date: dateString,
          assignedBy: currentUserId,
        };

        // Include allotment ID if updating existing exercise
        if (exercise.allotmentId) {
          exerciseAllotment.id = exercise.allotmentId;
        }

        exerciseArray.push(exerciseAllotment);
        console.log('Created exercise allotment:', exerciseAllotment);
      });
    });

    console.log('Generated exercise array:', exerciseArray);
    
    // Create the payload with fitness plan name if provided
    const payload: any = {
      exerciseAllotments: exerciseArray
    };
   
    
    if (fitnessPlanName && fitnessPlanName.trim() !== '') {
      payload.fitnessPlanName = fitnessPlanName.trim();
      console.log('✅ Added fitness plan name to payload:', fitnessPlanName);
    } else {
      console.log('❌ fitnessPlanName not added to payload - condition not met');
    }
    
    return payload;
  }

  /**
   * Validates all exercises across all dates and returns error messages
   */
  validateAllExercises(
    dateList: DateItem[],
    exerciseDataService: any
  ): string[] {
    const errors: string[] = [];

    // Get today's date for filtering (same logic as generateExerciseArray)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    dateList.forEach((dateItem, dateIndex) => {
      const dateDisplay = dateItem.formattedDate;

      // Skip validation for rest days
      if (dateItem.isRestDay) {
        return;
      }

      // Skip validation for past dates (they won't be sent to API)
      const itemDate = new Date(dateItem.date);
      itemDate.setHours(0, 0, 0, 0);

      if (itemDate < today) {
        return; // Skip past dates - they're not being updated
      }

      // Check each exercise in the date
      dateItem.exercises.forEach((exercise, exerciseIndex) => {
        // Skip empty exercises (exercises without a name)
        if (!exercise.exercise.trim()) {
          return;
        }

        const sectionName = exerciseDataService.getSectionDisplayName(
          exercise.section || 'main'
        );
        const exerciseIdentifier = `${dateDisplay} - ${sectionName} - ${exercise.exercise}`;

        // Check if the exercise has at least one valid set
        const hasValidSet = exercise.sets.some((set, setIndex) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          // Support both time string and separate fields
          let timeValue = '';
          if (typeof set.time === 'string') {
            timeValue = set.time.trim();
          } else if (
            (set.timeMinutes !== undefined &&
              set.timeMinutes !== null &&
              set.timeMinutes !== 0) ||
            (set.timeSeconds !== undefined &&
              set.timeSeconds !== null &&
              set.timeSeconds !== 0)
          ) {
            timeValue = '1'; // treat as present if either field is nonzero
          }

          const weightsValue =
            typeof set.weights === 'string'
              ? set.weights.trim()
              : set.weights !== null && set.weights !== undefined
              ? String(set.weights).trim()
              : '';

          // A set is valid if it has reps OR time (weights alone is not sufficient for a complete exercise)
          const hasValidReps =
            repsValue && repsValue !== '0' && repsValue !== '';
          const hasValidTime =
            timeValue && timeValue !== '0' && timeValue !== '';

          return hasValidReps || hasValidTime;
        });

        if (!hasValidSet) {
          errors.push(
            `${exerciseIdentifier}: Must have at least one set with either reps or time specified.`
          );
        }

        // Additional validation: Check for sets with only weights but no reps or time
        exercise.sets.forEach((set, setIndex) => {
          const repsValue =
            typeof set.reps === 'string'
              ? set.reps.trim()
              : set.reps !== null && set.reps !== undefined
              ? String(set.reps).trim()
              : '';

          // Treat 0:0 as empty for time
          const minutes = Number(set.timeMinutes) || 0;
          const seconds = Number(set.timeSeconds) || 0;
          const timeValue =
            typeof set.time === 'string'
              ? set.time.trim()
              : minutes > 0 || seconds > 0
              ? '1'
              : '';

          const weightsValue =
            typeof set.weights === 'string'
              ? set.weights.trim()
              : set.weights !== null && set.weights !== undefined
              ? String(set.weights).trim()
              : '';

          const hasValidReps =
            repsValue && repsValue !== '0' && repsValue !== '';
          const hasValidTime =
            timeValue && timeValue !== '0' && timeValue !== '';
          const hasValidWeights =
            weightsValue && weightsValue !== '0' && weightsValue !== '';

          // Warn if set only has weights without reps or time
          if (hasValidWeights && !hasValidReps && !hasValidTime) {
            const sectionName = exerciseDataService.getSectionDisplayName(
              exercise.section || 'main'
            );
            errors.push(
              `${dateDisplay} - ${sectionName} - ${exercise.exercise} - Set ${set.setNumber}: Set has weights but no reps or time. Please add reps or time.`
            );
          }
        });
      });
    });

    return errors;
  }

  /**
   * Deletes exercises that are marked for deletion
   */
  deleteMarkedExercises(dateList: DateItem[]): Observable<any> {
    const allotmentIdsToDelete: string[] = [];

    dateList.forEach((dateItem) => {
      dateItem.exercises.forEach((exercise) => {
        if (exercise.isMarkedForDeletion && exercise.allotmentId) {
          allotmentIdsToDelete.push(exercise.allotmentId);
        }
      });
    });

    if (allotmentIdsToDelete.length === 0) {
      // No exercises to delete, return a completed observable
      return new Observable((observer) => {
        observer.next({ message: 'No exercises marked for deletion' });
        observer.complete();
      });
    }

    console.log('Deleting marked exercises with IDs:', allotmentIdsToDelete);
    return this.fitnessPlanService.deleteExerciseAllotment(
      allotmentIdsToDelete
    );
  }

  /**
   * Removes exercises marked for deletion from the date list and clears rest day allotments from pending deletions
   */
  removeMarkedExercises(
    dateList: DateItem[],
    pendingDeletions?: Set<string>
  ): void {
    dateList.forEach((dateItem) => {
      // Remove exercises marked for deletion
      dateItem.exercises = dateItem.exercises.filter(
        (exercise) => !exercise.isMarkedForDeletion
      );

      // Clear rest day allotment ID if it was in pending deletions
      if (
        pendingDeletions &&
        dateItem.restDayAllotmentId &&
        pendingDeletions.has(dateItem.restDayAllotmentId)
      ) {
        console.log(
          `Clearing rest day allotment ID for ${dateItem.formattedDate}: ${dateItem.restDayAllotmentId}`
        );
        dateItem.restDayAllotmentId = undefined;
      }
    });
  }
}
